import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Link as ChakraLink,
  Flex,
  FlexProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { Fragment, useMemo, useRef } from "react";

import { useDrag } from "@nens/ranalib";
import { useOverflowItems } from "@nens/react/hooks";

import { removeEmptyPathSegments } from "@/utils/remove-empty-path-segments";

const DropdownIcon = () => (
  <Center py={3} h="full" alignItems="center" gap={1}>
    {[1, 2, 3].map((key) => (
      <Box key={key} bg="brand.analytic" flexShrink={0} boxSize={1} rounded="full" />
    ))}
  </Center>
);

interface PathProps extends FlexProps {
  path: string;
  dropdown?: boolean;
  onPathClick?: () => void;
  onDropInto?: (rowIds: string[]) => void;
  dropIds?: string[];
}
function PathLink({ path, dropdown, onPathClick, onDropInto, dropIds }: PathProps) {
  const hasLink = !!onPathClick;

  const { dropProps, dragProps } = useDrag({
    ids: dropIds ?? [],
    dragId: "file-drag",
    onDropInto,
  });

  return (
    <Flex
      pointerEvents="all"
      px={dropdown ? 4 : 2}
      h="button"
      alignItems="center"
      {...dragProps}
      {...dropProps}
    >
      <Box
        {...(!dropdown && hasLink ? { color: "brand.analytic", textDecoration: "underline" } : {})}
        {...(!dropdown && !hasLink ? { color: "brand.grey.600" } : {})}
        fontWeight={300}
        whiteSpace="nowrap"
        _hover={{ textDecoration: undefined }}
        onClick={onPathClick}
      >
        <Text
          {...(dropdown
            ? {
                maxW: 52,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
            : {})}
          {...(!dropdown && hasLink ? { as: ChakraLink } : {})}
        >
          {path}
        </Text>
      </Box>
    </Flex>
  );
}

type BreadcrumbPath = { path: string; idx: number; isFileName?: boolean };

interface FoldersBreadcrumbProps {
  paths: string[];
  fileName?: string;
  setPaths?: (paths: string[]) => void;
  isViewingFolder?: boolean;
  moveFilesByRowIds?: (rowIds: string[], destination: string) => void;
}

export function FoldersBreadcrumb({
  paths,
  fileName,
  setPaths,
  isViewingFolder,
  moveFilesByRowIds,
}: FoldersBreadcrumbProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const allPaths = useMemo<BreadcrumbPath[]>(() => {
    const result: BreadcrumbPath[] = removeEmptyPathSegments(paths).map((path, idx) => ({
      path,
      idx,
    }));
    if (fileName) result.push({ path: fileName, idx: result.length, isFileName: true });
    return result;
  }, [paths, fileName]);

  const {
    visibleItems: directPaths,
    overflowItems: dropdownPaths,
    isCalculating,
  } = useOverflowItems(parentRef, allPaths, { direction: "start" });

  const onPathClick = (pathIdx: number) => {
    let found = false;
    const newPaths = allPaths
      .map(({ idx, path }) => {
        if (found) return null;
        if (idx === pathIdx) found = true;
        return path;
      })
      .filter((path): path is string => !!path);
    setPaths && setPaths(newPaths);
  };

  const getDropProps = (pathIdx = -1) => {
    if (!moveFilesByRowIds) return {};

    const dropId = allPaths
      .slice(0, pathIdx + 1)
      .map(({ path, isFileName }) => `${path}${!isFileName ? "/" : ""}`)
      .join("");

    // Only allow dropping on dropdown paths, and not on the last path seeing that would be the current folder
    if (pathIdx >= paths.length - 1) return { dropIds: [dropId] };

    return {
      onDropInto: (rowIds: string[]) => moveFilesByRowIds(rowIds, dropId),
      dropIds: [dropId],
    };
  };

  const getOnPathClick = (path: BreadcrumbPath, isInDropdown: boolean) => {
    const canNavigate = !!setPaths;
    if (!canNavigate) return undefined;

    const ownPaths = isInDropdown ? dropdownPaths : directPaths;

    // Calculate how many items from the end should not be clickable
    const nonClickableItemsFromEnd = isViewingFolder && fileName ? 2 : 1; // If isViewingFolder is true and there is a fileName, the last folder will not be clickable seeing that the current page is the folder and not the file
    const lastClickableDropdownIndex = ownPaths.length - nonClickableItemsFromEnd;

    // A path is clickable if navigation is enabled and it's not one of the last items
    // Or if it's a dropdown and there are direct paths, meaning that even the last dropdown
    // item should be clickable because the last item is in the direct paths and not the dropdown paths
    const isClickableInOwnPaths = path.idx < lastClickableDropdownIndex;

    const hasDirectPathsShowing = directPaths.length > 0;
    const hasEnabledItemsInLaterPaths = isInDropdown && hasDirectPathsShowing;

    const isEnabled = isClickableInOwnPaths || hasEnabledItemsInLaterPaths;

    return isEnabled ? () => onPathClick(path.idx) : undefined;
  };

  return (
    <Flex
      ref={parentRef}
      px={4}
      flexDirection="row"
      rounded="base"
      borderWidth="base"
      h="button"
      overflow="hidden"
      alignItems="center"
    >
      <PathLink
        pr={4}
        path="Main folder"
        onPathClick={setPaths && allPaths.length !== 0 ? () => setPaths([]) : undefined}
        {...getDropProps()}
      />
      {dropdownPaths.length ? (
        <>
          <ChevronRightIcon />
          <Menu>
            <MenuButton borderWidth={0} bg="transparent" m={0} ml={4}>
              <DropdownIcon />
            </MenuButton>
            <MenuList>
              {dropdownPaths.map((path) => {
                const onPathClick = getOnPathClick(path, true);
                return (
                  <MenuItem
                    p={0}
                    key={"pathLink" + path.idx}
                    pointerEvents="none"
                    isDisabled={!onPathClick}
                    onClick={onPathClick}
                  >
                    <PathLink
                      path={path.path}
                      onPathClick={onPathClick}
                      {...getDropProps(path.idx)}
                      dropdown
                    />
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </>
      ) : null}
      {!!directPaths.length && (
        <Flex
          opacity={isCalculating ? 0 : 1}
          alignItems="center"
          gap={4}
          ml={dropdownPaths.length ? 4 : 0}
        >
          {directPaths.map((path) => (
            <Fragment key={"directPath_" + path.idx}>
              <ChevronRightIcon />
              <PathLink
                path={path.path}
                onPathClick={getOnPathClick(path, false)}
                {...getDropProps(path.idx)}
              />
            </Fragment>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
